import { sel } from "../../academy/selectors";
import { TutorialDefinition } from "../types";

/**
 * Agency portal onboarding: how to invite officers. Auto-starts the first
 * time an agency admin lands on the Officers tab (the dashboard default), and
 * can be replayed from "Take the tour". Every step only points at a control;
 * nothing is sent or changed. Steps whose target is not on screen are skipped
 * by the provider: no trial banner on a paid agency, and the investigator
 * step only exists when investigator seats were granted (the dashboard only
 * renders that anchor then).
 */
const tour: TutorialDefinition = {
  id: "agency-invite-officers",
  version: "1.0.0",
  releasedAt: "2026-11-03T00:00:00.000Z",
  category: "onboarding",
  audience: "agency",
  title: "Invite your officers",
  description: "How to send invitations and keep track of who has joined.",
  triggerRoute: "/agency/dashboard/officers",
  startRoute: "/agency/dashboard/officers",
  steps: [
    {
      target: sel("agencyTrialBanner"),
      title: "Your trial seats",
      content:
        "This shows how many officer seats your department has and when the evaluation ends. Invite up to that many officers.",
      disableBeacon: true,
      placement: "bottom",
    },
    {
      target: sel("agencyInviteEmails"),
      waitFor: sel("agencyInviteEmails"),
      title: "Add officer emails",
      content:
        "Type or paste each officer's email. Press Enter, Tab, Space or add a comma after each one and it becomes a chip. Use the address they will sign in with.",
      disableBeacon: true,
      placement: "top",
    },
    {
      target: sel("agencyInviteSend"),
      title: "Send the invitations",
      content:
        "Invite emails every officer on your list. Each one gets a personal link, and each invitation holds one seat until it is accepted or cancelled.",
      disableBeacon: true,
      placement: "left",
    },
    {
      target: sel("agencyOfficerTable"),
      title: "Track your officers",
      content:
        "This is where everyone shows up. Each invitation you send waits under Pending Invites (you can resend or cancel it), and once an officer registers they move to Members. It's empty now because nothing has been sent yet.",
      disableBeacon: true,
      placement: "top",
    },
    {
      target: sel("agencyInvestigatorSeats"),
      title: "Investigator seats",
      content:
        "Your trial includes investigator seats, which unlock Investigations. Once an officer is a member, choose Investigator from their role dropdown in Members to give them one of these seats.",
      disableBeacon: true,
      placement: "bottom",
    },
    {
      target: "body",
      title: "One important thing for your officers",
      content:
        "Officers must register from the invitation email. If they sign up on their own, their account may not be tied to your department's trial. You can replay this tour any time with Take the tour.",
      disableBeacon: true,
      placement: "center",
    },
  ],
};

export default tour;
